import { ComponentType, useMemo } from 'react';
import { CustomAttributeSidePanelRow } from 'src/components/CustomAttributes/side-panel-row';
import { CLIENT_SECTION_PROPERTIES } from 'src/components/SidePanelTabs/SiteClient/client-section-registry';
import { SITE_SECTION_PROPERTIES } from 'src/components/SidePanelTabs/SiteClient/site-section-registry';
import { useRelatedCustomAttributeDescriptors } from 'src/graphql/entities/custom-attributes/hooks/use-related-custom-attribute-descriptors';
import {
  buildCustomAttributeSidePanelDescriptors,
  useCustomAttributesSidePanel,
} from 'src/graphql/entities/custom-attributes/side-panel-context';
import { SITE_CARD_PROPERTY_KEYS } from 'src/graphql/entities/side-panel-personalization/model/card-keys';
import type {
  SidePanelRowProps,
  SidePanelSectionData,
  SidePanelSurfaceConfig,
} from 'src/graphql/entities/side-panel-personalization/model/descriptors';
import { UserSettingId } from 'src/graphql/entities/user-setting-value/user-setting-value.types';
import { useSiteCardForm } from './site-card-form-context';
import { SiteCardFieldRow, SiteCardLabelsRow } from './site-card-rows';

// on the Site page the base fields render as ordered panel rows (unlike the
// conversation surface, where the same catalog stays card-rendered)
const SITE_FIELD_ROW_BY_KEY: Record<string, ComponentType<SidePanelRowProps>> = {
  [SITE_CARD_PROPERTY_KEYS.serviceLevel]: SiteCardFieldRow,
  [SITE_CARD_PROPERTY_KEYS.accountManager]: SiteCardFieldRow,
  [SITE_CARD_PROPERTY_KEYS.labels]: SiteCardLabelsRow,
};

function useSiteSections(): SidePanelSectionData[] {
  const customAttributes = useCustomAttributesSidePanel();
  const clientAttributes = useRelatedCustomAttributeDescriptors('Client');
  const { isEmptyByKey } = useSiteCardForm();

  return useMemo(
    () => [
      {
        key: 'site' as const,
        label: 'Site',
        loading: customAttributes.loading,
        notice: customAttributes.payloadError ?? undefined,
        hidePanelLabel: true,
        properties: [
          ...SITE_SECTION_PROPERTIES.map((descriptor) => ({
            ...descriptor,
            Row: SITE_FIELD_ROW_BY_KEY[descriptor.key],
            isEmpty: isEmptyByKey[descriptor.key],
          })),
          ...buildCustomAttributeSidePanelDescriptors(
            customAttributes.rowsByPropertyKey,
            CustomAttributeSidePanelRow,
          ),
        ],
      },
      {
        key: 'client' as const,
        label: 'Client',
        loading: clientAttributes.loading,
        // the ClientCards on the Clients tab render their fields in layout order
        reorderable: true,
        properties: [...CLIENT_SECTION_PROPERTIES, ...clientAttributes.properties],
      },
    ],
    [
      customAttributes.loading,
      customAttributes.payloadError,
      customAttributes.rowsByPropertyKey,
      clientAttributes,
      isEmptyByKey,
    ],
  );
}

export const siteSidePanelSurface: SidePanelSurfaceConfig = {
  surfaceKey: 'site-page',
  settingIds: {
    profiles: UserSettingId.SiteSidePanelProfiles,
    personalization: UserSettingId.SiteSidePanelPersonalization,
    groupProfile: UserSettingId.SiteSidePanelGroupProfile,
  },
  useSections: useSiteSections,
};
